'use client';

import { getSession, useSession } from 'next-auth/react';
import { useCallback } from 'react';
import { apiDelete, apiDownload, apiGet, apiPatch, apiPost } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

type Params = Record<string, string | number | boolean | undefined>;

export function useApi() {
    const { data: session } = useSession();
    const token = session?.accessToken;

    const retryWithFreshToken = useCallback(
        async <T>(
            request: (accessToken?: string) => Promise<ApiResponse<T>>,
            response: ApiResponse<T>
        ) => {
            if (response.status !== 401) {
                return response;
            }

            const freshSession = await getSession();
            const freshToken = freshSession?.accessToken;

            if (!freshToken || freshToken === token) {
                return response;
            }

            return request(freshToken);
        },
        [token]
    );

    const get = useCallback(
        async <T>(endpoint: string, params?: Params, signal?: AbortSignal) => {
            const request = (accessToken?: string) => apiGet<T>(endpoint, accessToken, params, signal);
            return retryWithFreshToken(request, await request(token));
        },
        [retryWithFreshToken, token]
    );

    const post = useCallback(
        async <T>(endpoint: string, body: unknown, params?: Params, signal?: AbortSignal) => {
            const request = (accessToken?: string) => apiPost<T>(endpoint, body, accessToken, params, signal);
            return retryWithFreshToken(request, await request(token));
        },
        [retryWithFreshToken, token]
    );

    const patch = useCallback(
        async <T>(endpoint: string, body: unknown, params?: Params, signal?: AbortSignal) => {
            const request = (accessToken?: string) => apiPatch<T>(endpoint, body, accessToken, params, signal);
            return retryWithFreshToken(request, await request(token));
        },
        [retryWithFreshToken, token]
    );

    const del = useCallback(
        async <T>(endpoint: string, params?: Params, signal?: AbortSignal) => {
            const request = (accessToken?: string) => apiDelete<T>(endpoint, accessToken, params, signal);
            return retryWithFreshToken(request, await request(token));
        },
        [retryWithFreshToken, token]
    );

    const download = useCallback(
        (endpoint: string, params?: Params, body?: unknown) => apiDownload(endpoint, token, params, body),
        [token]
    );

    return { get, post, patch, del, download };
}
